import i18next from 'i18next';

import * as yup from 'yup';

import {PackageTypes} from 'types/gtins';

const BATCH_NUMBER_PATTERN = /^[A-Z0-9-]{1,20}$/;
export const getMaxMedicationExpirationDate = (currentDate = new Date()) =>
    new Date(currentDate.getFullYear() + 10, 11, 31, 23, 59, 59, 999);

const isExpirationOnOrAfterManufacturing = (expirationDate?: string, manufacturingDate?: string) =>
    !expirationDate ||
    !manufacturingDate ||
    new Date(expirationDate).getTime() >= new Date(manufacturingDate).getTime();
const isExpirationWithinAllowedRange = (expirationDate?: string) => {
    if (!expirationDate) return true;

    const expirationTimestamp = new Date(expirationDate).getTime();
    return (
        Number.isNaN(expirationTimestamp) ||
        expirationTimestamp <= getMaxMedicationExpirationDate().getTime()
    );
};

export const productsSchema = yup
    .array()
    .of(
        yup
            .object({
                gtin: yup.string().required(`${i18next.t('components.form.required')}: GTIN`),
                quantity: yup
                    .number()
                    .typeError(
                        `${i18next.t('components.form.wrong')} ${i18next.t(
                            'pages.orders.form.quantity',
                        )}`,
                    )
                    .positive(
                        `${i18next.t('components.form.wrong')} ${i18next.t(
                            'pages.orders.form.quantity',
                        )}: ${'${value}'}`,
                    )
                    .integer(
                        `${i18next.t('components.form.wrong')} ${i18next.t(
                            'pages.orders.form.quantity',
                        )}: ${'${value}'}`,
                    )
                    .max(
                        10000,
                        `${i18next.t('components.form.wrong')} ${i18next.t(
                            'pages.orders.form.quantity',
                        )}: ${'${value}'}`,
                    )
                    .required(
                        `${i18next.t('components.form.required')}: ${i18next.t(
                            'pages.orders.form.quantity',
                        )}`,
                    ),
                type: yup
                    .string()
                    .oneOf(
                        // ['UNIT', 'GROUP'],
                        Object.keys(PackageTypes),
                        `${i18next.t('components.form.wrong')} ${i18next.t(
                            'pages.orders.form.type',
                        )}: ${'${value}'}`,
                    )
                    .required(
                        `${i18next.t('components.form.required')}: ${i18next.t(
                            'pages.orders.form.type',
                        )}`,
                    ),
            })
            // @ts-ignore
            .uniqueProperty('gtin', ' '),
    )
    .min(1, `${i18next.t('components.form.min')} 1`)
    .max(10, `${i18next.t('components.form.max')} 10`)
    .test(
        'single-package-type',
        i18next.t('components.form.onlyOnePackageType'),
        (products) => new Set(products?.map(({type}) => type).filter(Boolean)).size <= 1,
    );

const gtinOptionSchema = yup
    .object({
        value: yup.string().required(),
        label: yup.string().required(),
        meta: yup.object({
            productVersionId: yup
                .string()
                .nullable()
                .default(''),
            productName: yup
                .string()
                .nullable()
                .default(''),
            productGroupCode: yup
                .string()
                .nullable()
                .default(''),
            type: yup
                .mixed<keyof typeof PackageTypes>()
                .oneOf(Object.keys(PackageTypes) as Array<keyof typeof PackageTypes>)
                .required(),
        }),
    })
    .nullable();

export type QuantityValidationContext = {
    balance?: number;
    codesLeft?: number;
};

const isMedicationOption = (option: any) => option?.meta?.productGroupCode === 'MEDICATIONS';

export const productsSchemaNew = yup.object({
    gtin: gtinOptionSchema.required('pages.orders.form.errors.requiredGtin'),

    quantity: yup
        .number()
        .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? null : value,
        )
        .nullable()
        .typeError('pages.orders.form.errors.quantityInteger')
        .positive('pages.orders.form.errors.quantity')
        .integer('pages.orders.form.errors.quantityInteger')
        .max(10000, 'pages.orders.form.errors.quantityMax10000')
        .test(
            'quantity-max-balance',
            'pages.orders.form.errors.quantityMaxBalance',
            function checkAgainstBalance(value) {
                const {balance} = (this.options.context ?? {}) as QuantityValidationContext;
                if (value == null || balance == null) return true;
                return value <= balance;
            },
        )
        .test(
            'quantity-max-codes-left',
            'pages.orders.form.errors.quantityMaxCodesLeft',
            function checkAgainstCodesLeft(value) {
                const {codesLeft} = (this.options.context ?? {}) as QuantityValidationContext;
                if (value == null || codesLeft == null) return true;
                return value <= codesLeft;
            },
        )
        .required('pages.orders.form.errors.requiredQuantity'),
    manufacturingDate: yup.string().when('gtin', {
        is: isMedicationOption,
        then: (fieldSchema) =>
            fieldSchema.required('pages.orders.form.errors.requiredManufacturingDate'),
        otherwise: (fieldSchema) => fieldSchema.notRequired(),
    }),
    expirationDate: yup.string().when('gtin', {
        is: isMedicationOption,
        then: (fieldSchema) =>
            fieldSchema
                .required('pages.orders.form.errors.requiredExpirationDate')
                .test(
                    'valid-expiration-date',
                    'pages.orders.form.errors.invalidExpirationDate',
                    (value) => !value || !Number.isNaN(new Date(value).getTime()),
                )
                .test(
                    'expiration-after-manufacturing',
                    'pages.orders.form.errors.expirationDateAfterManufacturing',
                    function validateExpirationDate(value) {
                        return isExpirationOnOrAfterManufacturing(
                            value,
                            this.parent.manufacturingDate,
                        );
                    },
                )
                .test(
                    'expiration-max-date',
                    'pages.orders.form.errors.expirationDateMax',
                    isExpirationWithinAllowedRange,
                ),
        otherwise: (fieldSchema) => fieldSchema.notRequired(),
    }),
    batchNumber: yup.string().when('gtin', {
        is: isMedicationOption,
        then: (fieldSchema) =>
            fieldSchema
                .required('pages.orders.form.errors.requiredBatchNumber')
                .test(
                    'valid-batch-number',
                    'pages.orders.form.errors.invalidBatchNumber',
                    (value) => !value || BATCH_NUMBER_PATTERN.test(value),
                )
                .test(
                    'different-medication-batch',
                    'pages.orders.form.errors.batchNumbersMustDiffer',
                    function validateMedicationBatch(value) {
                        if (!value) return true;

                        const {gtin, products = []} = this.parent;
                        return !products.some(
                            (product: any) =>
                                product.productGroupCode === 'MEDICATIONS' &&
                                product.type !== gtin?.meta?.type &&
                                product.batchNumber?.toUpperCase() === value.toUpperCase(),
                        );
                    },
                ),
        otherwise: (fieldSchema) => fieldSchema.notRequired(),
    }),
    products: yup
        .array()
        .of(
            yup.object({
                productGroupCode: yup.string(),
                type: yup.string(),
                manufacturingDate: yup.string().when('productGroupCode', {
                    is: 'MEDICATIONS',
                    then: (fieldSchema) =>
                        fieldSchema.required('pages.orders.form.errors.requiredManufacturingDate'),
                    otherwise: (fieldSchema) => fieldSchema.notRequired(),
                }),
                expirationDate: yup.string().when('productGroupCode', {
                    is: 'MEDICATIONS',
                    then: (fieldSchema) =>
                        fieldSchema
                            .required('pages.orders.form.errors.requiredExpirationDate')
                            .test(
                                'valid-expiration-date',
                                'pages.orders.form.errors.invalidExpirationDate',
                                (value) => !value || !Number.isNaN(new Date(value).getTime()),
                            )
                            .test(
                                'expiration-after-manufacturing',
                                'pages.orders.form.errors.expirationDateAfterManufacturing',
                                function validateExpirationDate(value) {
                                    return isExpirationOnOrAfterManufacturing(
                                        value,
                                        this.parent.manufacturingDate,
                                    );
                                },
                            )
                            .test(
                                'expiration-max-date',
                                'pages.orders.form.errors.expirationDateMax',
                                isExpirationWithinAllowedRange,
                            ),
                    otherwise: (fieldSchema) => fieldSchema.notRequired(),
                }),
                batchNumber: yup.string().when('productGroupCode', {
                    is: 'MEDICATIONS',
                    then: (fieldSchema) =>
                        fieldSchema
                            .required('pages.orders.form.errors.requiredBatchNumber')
                            .test(
                                'valid-batch-number',
                                'pages.orders.form.errors.invalidBatchNumber',
                                (value) => !value || BATCH_NUMBER_PATTERN.test(value),
                            ),
                    otherwise: (fieldSchema) => fieldSchema.notRequired(),
                }),
            }),
        )
        .test('different-medication-batches', function validateMedicationBatches(products) {
            if (!products) return true;

            for (let index = 0; index < products.length; index += 1) {
                const product = products[index];
                if (product.productGroupCode !== 'MEDICATIONS' || !product.batchNumber) continue;

                const hasSameBatchForAnotherType = products.some(
                    (candidate, candidateIndex) =>
                        candidateIndex !== index &&
                        candidate.productGroupCode === 'MEDICATIONS' &&
                        candidate.type !== product.type &&
                        candidate.batchNumber?.toUpperCase() === product.batchNumber?.toUpperCase(),
                );

                if (hasSameBatchForAnotherType) {
                    return this.createError({
                        path: `products[${index}].batchNumber`,
                        message: 'pages.orders.form.errors.batchNumbersMustDiffer',
                    });
                }
            }

            return true;
        })
        .test(
            'single-package-type',
            'components.form.onlyOnePackageType',
            (products) => new Set(products?.map(({type}) => type).filter(Boolean)).size <= 1,
        )
        .test('unique-gtin', 'components.form.duplicateGtin', (products) => {
            const gtins = products?.map(({gtin}: any) => gtin).filter(Boolean) ?? [];
            return new Set(gtins).size === gtins.length;
        }),
});
